import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import {
  ArrowRight,
  CheckCircle2,
  ArrowLeft,
  Users,
  Clock,
  Wallet,
  FileText,
  FileCheck2,
} from "lucide-react";
import { getService, services, UNIVERSAL_OFFERINGS } from "@/lib/services-data";
import { getServiceConversion } from "@/lib/service-conversion";
import { buildFaqPageJsonLd, getServiceFaqs } from "@/lib/service-faqs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

import { Disclaimer } from "@/components/site/Disclaimer";
import { RelatedServices } from "@/components/site/RelatedServices";


export const Route = createFileRoute("/services/$slug")({
  loader: ({ params }) => {
    const service = getService(params.slug);
    if (!service) throw notFound();
    return { service };
  },
  head: ({ params, loaderData }) => {
    const s = loaderData?.service;
    const canonical = `https://nkadimengdannylcs.com/services/${params.slug}`;
    const SUFFIX = " | Nkadimeng Danny Legal";
    const base = s?.title ?? "Service";
    const maxBase = 60 - SUFFIX.length;
    const title =
      (base.length > maxBase ? `${base.slice(0, maxBase - 1).trimEnd()}…` : base) + SUFFIX;
    const desc = s?.short ?? "Legal and compliance support in South Africa.";
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:type", content: "website" },
        { property: "og:url", content: canonical },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: canonical }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(buildFaqPageJsonLd(params.slug, canonical)),
        },
      ],
    };

  },
  notFoundComponent: () => (
    <div className="container-narrow py-24 text-center">
      <h1 className="font-display text-3xl text-navy">Service not found</h1>
      <Link to="/services" className="mt-4 inline-block text-navy underline">
        View all services
      </Link>
    </div>
  ),
  component: ServiceDetail,
});

function ServiceDetail() {
  const { service } = Route.useLoaderData();
  const conversion = getServiceConversion(service.slug);
  const related = services.filter((s) => s.slug !== service.slug).slice(0, 3);
  const faqs = getServiceFaqs(service.slug);



  return (
    <>
      <section className="border-b border-border bg-secondary/40">
        <div className="container-narrow py-16 md:py-20">
          <Link
            to="/services"
            className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-navy"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> All services
          </Link>
          <div className="mt-6 flex items-start gap-5">
            <div className="hidden sm:inline-flex h-14 w-14 items-center justify-center rounded-md bg-navy text-gold">
              <service.icon className="h-7 w-7" />
            </div>
            <div>
              <span className="gold-rule" />
              <h1 className="font-display text-4xl text-navy md:text-5xl">
                {service.title}
              </h1>
              <p className="mt-3 max-w-2xl text-lg text-muted-foreground">
                {service.intro}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="container-narrow grid gap-12 py-16 md:grid-cols-[1.4fr_1fr]">
        <div className="space-y-10">
          <div>
            <h2 className="font-display text-2xl text-navy">Overview</h2>
            <p className="mt-3 text-muted-foreground leading-relaxed">
              {service.details}
            </p>
          </div>

          <div>
            <h2 className="font-display text-2xl text-navy flex items-center gap-2">
              <Users className="h-5 w-5 text-gold" /> Who this is for
            </h2>
            <ul className="mt-4 grid gap-2 sm:grid-cols-2">
              {service.whoFor.map((w: string) => (
                <li
                  key={w}
                  className="flex items-start gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm"
                >
                  <CheckCircle2 className="mt-0.5 h-4 w-4 text-gold" />
                  <span>{w}</span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h2 className="font-display text-2xl text-navy">What we offer</h2>
            <ul className="mt-6 space-y-3">
              {service.offerings.map((o: string) => (
                <li
                  key={o}
                  className="flex items-start gap-3 rounded-lg border border-border bg-card p-4 shadow-soft"
                >
                  <CheckCircle2 className="mt-0.5 h-5 w-5 text-gold" />
                  <span className="text-foreground">{o}</span>
                </li>
              ))}
            </ul>
            <div className="mt-4 rounded-lg border border-gold/40 bg-gold/5 p-4">
              <p className="text-sm font-medium text-navy">
                Included with every service
              </p>
              <ul className="mt-2 space-y-2">
                {UNIVERSAL_OFFERINGS.map((o) => (
                  <li key={o} className="flex items-start gap-2 text-sm text-foreground">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                    <span>{o}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div>
            <h2 className="font-display text-2xl text-navy">What you receive</h2>
            <div className="mt-6 grid gap-4 md:grid-cols-3">
              <div className="rounded-lg border border-border bg-card p-5 shadow-soft md:col-span-3">
                <p className="text-sm font-medium text-navy">Deliverables</p>
                <ul className="mt-3 grid gap-2 sm:grid-cols-2">
                  {conversion.deliverables.map((d) => (
                    <li key={d} className="flex items-start gap-2 text-sm text-foreground">
                      <FileCheck2 className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                      <span>{d}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-lg border border-border bg-card p-5 shadow-soft">
                <p className="flex items-center gap-2 text-sm font-medium text-navy">
                  <Clock className="h-4 w-4 text-gold" /> Turnaround
                </p>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                  {conversion.turnaround}
                </p>
              </div>
              <div className="rounded-lg border border-border bg-card p-5 shadow-soft">
                <p className="flex items-center gap-2 text-sm font-medium text-navy">
                  <Wallet className="h-4 w-4 text-gold" /> Indicative fee
                </p>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                  {conversion.pricingHint}
                </p>
              </div>
              <div className="rounded-lg border border-border bg-card p-5 shadow-soft">
                <p className="flex items-center gap-2 text-sm font-medium text-navy">
                  <FileText className="h-4 w-4 text-gold" /> Sample output
                </p>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                  {conversion.sampleOutput}
                </p>
              </div>
            </div>
            <p className="mt-3 text-xs text-muted-foreground">
              Fees are indicative. You receive a fixed written quote before any work begins — no
              hourly surprises.
            </p>
          </div>


          <div>
            <h2 className="font-display text-2xl text-navy">How we work</h2>
            <ol className="mt-6 grid gap-4 sm:grid-cols-2">
              {service.process.map((p: { title: string; body: string }) => (
                <li
                  key={p.title}
                  className="rounded-lg border border-border bg-card p-5 shadow-soft"
                >
                  <p className="font-display text-base text-navy">{p.title}</p>
                  <p className="mt-1 text-sm text-muted-foreground leading-relaxed">
                    {p.body}
                  </p>
                </li>
              ))}
            </ol>
          </div>

          <div>
            <h2 className="font-display text-2xl text-navy">
              Frequently asked questions
            </h2>
            <Accordion type="single" collapsible className="mt-4 w-full">
              {faqs.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`}>
                  <AccordionTrigger className="text-left font-display text-base text-navy">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-muted-foreground leading-relaxed">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>


          <div>
            <h2 className="font-display text-2xl text-navy">Scope of this service</h2>
            <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
              {service.scope}
            </p>
            <div className="mt-4">
              <Disclaimer variant="compact" />
            </div>
          </div>
        </div>

        <aside className="h-fit space-y-5 md:sticky md:top-28">
          <div className="rounded-xl bg-navy p-8 text-navy-foreground shadow-lifted">
            <h3 className="font-display text-xl">Ready to get started?</h3>
            <p className="mt-3 text-sm text-navy-foreground/80">
              {service.cta
                ? `Jump straight to the ${service.title.toLowerCase()} page.`
                : `Book a consultation and let's discuss how we can help with your ${service.title.toLowerCase()} matter.`}
            </p>
            {service.cta ? (
              <Link
                to={service.cta.to as any}
                className="mt-6 inline-flex items-center gap-2 rounded-md bg-gold px-5 py-2.5 text-sm font-semibold text-gold-foreground"
              >
                {service.cta.label} <ArrowRight className="h-4 w-4" />
              </Link>
            ) : (
              <Link
                to="/book"
                search={{ service: service.slug }}
                className="mt-6 inline-flex items-center gap-2 rounded-md bg-gold px-5 py-2.5 text-sm font-semibold text-gold-foreground"
              >
                Book consultation <ArrowRight className="h-4 w-4" />
              </Link>
            )}
            <Link
              to="/contact"
              className="mt-3 inline-flex items-center gap-2 text-sm text-navy-foreground/80 hover:text-gold"
            >
              Or send a message
            </Link>
          </div>

          <div className="rounded-xl border border-border bg-card p-6 shadow-soft">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gold">
              Good to know
            </p>
            <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
              <li>• Initial consultation fees are quoted up front.</li>
              <li>• In-person, telephone, Teams or Zoom.</li>
              <li>• Written letter of engagement before work begins.</li>
              <li>• Confidential and POPIA-aligned.</li>
            </ul>
          </div>
        </aside>
      </section>

      <section className="bg-secondary/50 py-16">
        <div className="container-narrow">
          <h2 className="font-display text-2xl text-navy">Other services</h2>
          <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((s) => (
              <Link
                key={s.slug}
                to="/services/$slug"
                params={{ slug: s.slug }}
                className="group rounded-xl border border-border bg-card p-6 shadow-soft hover:-translate-y-0.5 hover:shadow-lifted transition"
              >
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-gold/15 text-navy">
                  <s.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-3 font-display text-lg text-navy">
                  {s.title}
                </h3>
                <p className="mt-2 text-sm text-muted-foreground">{s.short}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <RelatedServices exclude={["/services"]} limit={3} />
    </>
  );
}
